import { useEffect, useMemo, useState } from "react";
import {
    Modal, ModalBody, ModalFooter, ModalHeader,
    Form, FormGroup, Label, Input, Button, Spinner, Alert, FormText
} from "reactstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { getOpportunityAccounts, getOpportunityUsers } from "../../../data/opportunities/opportunitiesSlice";
import { createQuote, updateQuote } from "../data/quoteService"; // <-- add updateQuote
import { createAudit } from "../../audit/data/auditService";
import { findChangesInObject } from "../../../utils/objectUtils";
import { createNewQuoteFolderStructure } from '../../sharepoint/data/sharepointService';
import { toast } from 'react-toastify';
import { getQuoteFileName } from "../functions/quoteFunctions";

/**
 * Props:
 *  - modalOpen, setModalOpen
 *  - opportunity: { Id, OpportunityName, AccountName }
 *  - quote?: existing quote (if provided, we edit)
 */
const QuoteModal = ({ modalOpen, setModalOpen, opportunity, quote, totalRecords }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const isEdit = Boolean(quote?.id); // 🔐 decide mode

    // Cached references (optional, as you already had)
    const opportunityAccounts = useSelector(getOpportunityAccounts);
    const opportunityUsers = useSelector(getOpportunityUsers);

    // Resolve account (optional – keep for prefill parity)
    const resolvedAccount = useMemo(() => {
        const accountId =
            opportunity?.AccountId ??
            opportunity?.accountId ??
            opportunity?.Account?.Id ?? "";
        if (!accountId) return null;
        return (opportunityAccounts || []).find(a => a.Id === accountId) || null;
    }, [opportunity, opportunityAccounts]);

    // Resolve owner (optional)
    const resolvedOwner = useMemo(() => {
        const ownerId = opportunity?.OwnerId ?? opportunity?.ownerId ?? "";
        if (!ownerId) return null;
        return (opportunityUsers || []).find(u => u.Id === ownerId) || null;
    }, [opportunity, opportunityUsers]);

    // ---------- Prefill ----------
    const defaults = useMemo(() => {
        if (isEdit) {
            // Prefill from existing quote
            return {
                Name: quote?.name ?? "",
                SalesforceOpportunityId: quote?.salesforceOpportunityId ?? opportunity?.Quote_Number__c ?? "",
                SalesforceOpportunityName: quote?.salesforceOpportunityName ?? opportunity?.Name ?? "",
                SalesforceAccountName: quote?.salesforceAccountName ?? opportunity?.AccountName ?? resolvedAccount?.Name ?? "",
                isActive: !!quote?.isActive,
            };
        }
        // Create mode
        return {
            Name: "",
            SalesforceOpportunityId: opportunity?.Quote_Number__c ?? "",
            SalesforceOpportunityName: opportunity?.Name ?? "",
            SalesforceAccountName: opportunity?.AccountName ?? resolvedAccount?.Name ?? "",
            isActive: totalRecords <= 0 ? true : false,
        };
    }, [isEdit, quote, opportunity, resolvedAccount, totalRecords]);

    const [form, setForm] = useState(defaults);
    const [submitted, setSubmitted] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (modalOpen) {
            setForm(defaults);
            setSubmitted(false);
            setErrorMsg("");
        }
    }, [modalOpen, defaults]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    // ---------- Mutation ----------
    const { mutateAsync, isLoading } = useMutation({
        mutationFn: async () => {
            let result;
            // basic validation
            if (!form.Name?.trim()) throw new Error("Name is required.");
            if (!form.SalesforceOpportunityId?.trim())
                throw new Error("SalesforceOpportunityId is required.");

            // Be consistent with your API’s expected casing (you used `IsActive` in update elsewhere)
            const payload = {
                Name: form.Name.trim(),
                SalesforceOpportunityId: form.SalesforceOpportunityId.trim(),
                SalesforceOpportunityName: form.SalesforceOpportunityName?.trim() ?? "",
                SalesforceAccountName: form.SalesforceAccountName?.trim() ?? "",
                IsActive: !!form.isActive,
            };
            
            result = isEdit ? await updateQuote(quote.id, payload) : await createQuote(payload);

            payload.Id = isEdit ? quote.id : result.data.newId; // for auditing
            payload.SharePoint_Folder__c = opportunity.SharePoint_Folder__c; // for auditing
            if (!isEdit) {
                const revisionNumber = 1;
                const quoteFileName = getQuoteFileName(payload.Id, revisionNumber, payload.SalesforceOpportunityName);
                const { message, responseOk } = await createNewQuoteFolderStructure(opportunity.SharePoint_Folder__c, form.isActive, payload.Id, revisionNumber, quoteFileName);
                if (!responseOk) {
                    toast.error(message);
                    console.error(`Quote ${payload.Id} revision ${revisionNumber} - ${message}`);
                    createAudit({ "action": "Create quote in sharepoint", "result": "Error", "source": `quote.${payload.Id}`, "payload": JSON.stringify({ ...payload, message }) });
                }
                else
                    createAudit({ "action": "Create quote in sharepoint", "result": "Successful", "source": `quote.${payload.Id}`, "payload": JSON.stringify(payload) });
            }
            auditChanges(isEdit, payload);

            return result;
        },
        onSuccess: async () => {
            // Invalidate both the single quote and lists
            await queryClient.invalidateQueries({ queryKey: ["quote"] });               // single quote (any)
            await queryClient.invalidateQueries({ queryKey: ["quote", quote?.id] });    // this one
            await queryClient.invalidateQueries({ queryKey: ["quotes"] });              // generic list
            await queryClient.invalidateQueries({ queryKey: ["quotesList"] });          // any alt list key
            if (form.SalesforceOpportunityId) {
                await queryClient.invalidateQueries({
                    queryKey: ["quotes", "byOpportunity", form.SalesforceOpportunityId],
                });
            }
            setModalOpen(false);
            setIsSaving(false);
        },
        onError: (err) => setErrorMsg(err?.message || (isEdit ? "Failed to update quote." : "Failed to create quote.")),
    });

    const auditChanges = (isEdit, payload) => {
        let diffs = {};
        if (isEdit) {
            diffs = findChangesInObject(quote, payload, false, true);
            if (Object.keys(diffs).length === 0) return;
        }
        
        const action = isEdit ? "Quote updated" : "Quote created";
        const content = isEdit ? JSON.stringify(diffs) : JSON.stringify(payload);
        createAudit({ "action": action, "result": "Successful", "source": `quote.${payload.Id}`, "payload": content })
    };

    const handleSave = async (e) => {

        setIsSaving(true);
        e.preventDefault();
        setSubmitted(true);
        setErrorMsg("");

        const result = await mutateAsync();
        if (!isEdit) {
            const newQuoteId = result.data.newId;
            navigate(`/opportunities/${form.SalesforceOpportunityId.trim()}/${newQuoteId}`);
        }
    };

    const handleCancel = () => setModalOpen(false);

    const nameInvalid = submitted && !form.Name?.trim();
    const oppIdInvalid = submitted && !form.SalesforceOpportunityId?.trim();

    return (
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} centered size="lg" scrollable backdrop="static">
            <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                {isEdit ? "Edit Quote" : "Create New Quote"}
            </ModalHeader>

            <ModalBody>
                {errorMsg && <Alert color="danger" className="mb-3">{errorMsg}</Alert>}

                <Form onSubmit={handleSave}>
                    <FormGroup>
                        <Label for="Name">Name <span className="text-danger">*</span></Label>
                        <Input
                            id="Name"
                            name="Name"
                            value={form.Name}
                            onChange={onChange}
                            placeholder="Enter a descriptive name"
                            invalid={!!nameInvalid}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="SalesforceOpportunityId">Salesforce Opportunity ID <span className="text-danger">*</span></Label>
                        <Input
                            id="SalesforceOpportunityId"
                            name="SalesforceOpportunityId"
                            value={form.SalesforceOpportunityId}
                            onChange={onChange}
                            readOnly
                            disabled
                            invalid={!!oppIdInvalid}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="SalesforceOpportunityName">Salesforce Opportunity Name</Label>
                        <Input
                            id="SalesforceOpportunityName"
                            name="SalesforceOpportunityName"
                            value={form.SalesforceOpportunityName}
                            onChange={onChange}
                            readOnly
                            disabled
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="SalesforceAccountName">Salesforce Account Name</Label>
                        <Input
                            id="SalesforceAccountName"
                            name="SalesforceAccountName"
                            value={form.SalesforceAccountName}
                            onChange={onChange}
                            readOnly
                            disabled
                        />
                    </FormGroup>

                    <FormGroup check className="mt-2">
                        <Input
                            type="checkbox"
                            id="isActive"
                            name="isActive"
                            checked={!!form.isActive}
                            onChange={onChange}
                            disabled={totalRecords <= 1}
                        />
                        <Label for="isActive" check>Active</Label>
                        <FormText color="muted" className="d-block">
                            This is the main quote for the opportunity. Only one quote can be active at a time.
                        </FormText>
                    </FormGroup>
                </Form>
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" outline onClick={handleCancel} disabled={isLoading || isSaving}>Cancel</Button>
                <Button color="primary" onClick={handleSave} disabled={isLoading || isSaving}>
                    {isLoading || isSaving ? (<><Spinner size="sm" className="me-2" />{isLoading && "Loading…"}{isSaving && "Saving..."}</>) : (isEdit ? "Save changes" : "Create quote")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default QuoteModal;
